/**
 * Unified wishlist composable that keeps local and API wishlists synchronized
 * For logged-in users: API is primary, local is kept in sync
 * For guest users: Local storage is used
 */
export default function useUnifiedWishlist() {
    const { isLoggedIn } = useUser();
    const localWishlist = useLocalWishlist();
    const syncWishlist = useSyncWishlist();
    const wishlistSidebarStore = useWishlistSidebarStore();

    // Minimal unauthorized check to fall back to local wishlist when session is stale
    const isUnauthorizedError = (err: any) => {
        const status = err?.response?.status ?? err?.status ?? err?.statusCode;
        return status === 401 || status === 403;
    };

    /**
     * Client-side cache so the wishlist count can show immediately on first paint.
     * We keep it in sync whenever items change, and refresh on explicit refresh triggers.
     */
    const CACHE_ITEMS_KEY = 'sw-wishlist-items';
    const CACHE_COUNT_KEY = 'sw-wishlist-count';
    const CACHE_UPDATED_AT_KEY = 'sw-wishlist-updated-at';

    const isClient = typeof window !== 'undefined';
    const cachedItems = ref<string[]>([]);
    const cachedCount = ref<number | null>(null);
    const hasLoadedApiWishlist = ref(false);
    const skipNextRefreshFetch = ref(false);

    const safeParseJsonArray = (raw: string | null): string[] => {
        if (!raw) return [];
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed.filter(x => typeof x === 'string') : [];
        } catch {
            return [];
        }
    };

    const readCache = () => {
        if (!isClient) return;
        try {
            const itemsRaw = window.localStorage?.getItem(CACHE_ITEMS_KEY) ?? null;
            const countRaw = window.localStorage?.getItem(CACHE_COUNT_KEY) ?? null;
            const items = safeParseJsonArray(itemsRaw);
            cachedItems.value = items;

            const parsedCount = countRaw !== null ? Number(countRaw) : NaN;
            cachedCount.value = Number.isFinite(parsedCount) ? parsedCount : items.length || 0;
        } catch {
            // ignore storage errors
        }
    };

    const writeCache = (items: string[]) => {
        if (!isClient) return;
        try {
            window.localStorage?.setItem(CACHE_ITEMS_KEY, JSON.stringify(items));
            window.localStorage?.setItem(CACHE_COUNT_KEY, String(items.length));
            window.localStorage?.setItem(CACHE_UPDATED_AT_KEY, String(Date.now()));
        } catch {
            // ignore storage errors
        }
    };

    const updateCacheFromItems = (items: string[]) => {
        const normalized = Array.from(new Set((items ?? []).filter(x => typeof x === 'string')));
        cachedItems.value = normalized;
        cachedCount.value = normalized.length;
        writeCache(normalized);
    };

    // Unified interface that works for both logged-in and guest users
    // Cache for prefetch
    const prefetchedWishlist = ref<any[]>([]);
    const items = computed(() => {
        // Use prefetched data if available and logged in
        if (isLoggedIn.value && prefetchedWishlist.value.length > 0) {
            return prefetchedWishlist.value;
        }
        if (isLoggedIn.value) {
            // If API hasn't loaded yet, fall back to cached items for instant UI
            if ((syncWishlist.items.value?.length ?? 0) > 0) return syncWishlist.items.value;
            if (cachedItems.value.length > 0) return cachedItems.value;
            return syncWishlist.items.value;
        }
        // Guest user: local storage wishlist (and cache as a fallback until it hydrates)
        if ((localWishlist.items.value?.length ?? 0) > 0) return localWishlist.items.value;
        if (cachedItems.value.length > 0) return cachedItems.value;
        return localWishlist.items.value;
    });

    const count = computed(() => {
        // Prefer instant, deterministic sources first
        if (prefetchedWishlist.value.length > 0) return prefetchedWishlist.value.length;

        if (isLoggedIn.value) {
            // If API wishlist has loaded, its count is authoritative (even if it's 0)
            if (hasLoadedApiWishlist.value) return syncWishlist.count.value;

            // Before API is ready, use cached count/items so badge doesn't "lag"
            if (cachedCount.value !== null) return cachedCount.value;
            if (cachedItems.value.length > 0) return cachedItems.value.length;
            return syncWishlist.count.value;
        }

        // Guest user
        const localCount = localWishlist.count.value;
        if (localCount > 0) return localCount;
        if (cachedCount.value !== null) return cachedCount.value;
        if (cachedItems.value.length > 0) return cachedItems.value.length;
        return localCount;
    });

    const getWishlistProducts = async () => {
        if (isLoggedIn.value) {
            try {
                await syncWishlist.getWishlistProducts();
                hasLoadedApiWishlist.value = true;
                // Sync local storage with API data
                const currentItems = syncWishlist.items.value;
                updateCacheFromItems(currentItems);
                // Cache for instant UI
                prefetchedWishlist.value = [...syncWishlist.items.value];
            } catch (err: any) {
                if (isUnauthorizedError(err)) {
                    console.warn('[Wishlist] Unauthorized when fetching API wishlist; using local.');
                    localWishlist.getWishlistProducts();
                    prefetchedWishlist.value = [...localWishlist.items.value];
                    updateCacheFromItems(prefetchedWishlist.value);
                } else {
                    throw err;
                }
            }
        } else {
            localWishlist.getWishlistProducts();
            prefetchedWishlist.value = [...localWishlist.items.value];
            updateCacheFromItems(prefetchedWishlist.value);
        }
    };
    // Prefetch wishlist on login
    watch(isLoggedIn, async (newVal, oldVal) => {
        if (newVal && !oldVal) {
            await getWishlistProducts();
        }
        if (!newVal) {
            prefetchedWishlist.value = [];
            hasLoadedApiWishlist.value = false;
        }
    });

    // Hydrate cache immediately on client so count can be shown before API responds
    if (isClient) {
        readCache();
    }

    // If any component requests a refresh, re-fetch and refresh the cache
    if (isClient) {
        watch(
            () => wishlistSidebarStore.refreshTrigger,
            async () => {
                if (skipNextRefreshFetch.value) {
                    skipNextRefreshFetch.value = false;
                    return;
                }
                try {
                    await getWishlistProducts();
                } catch {
                    // ignore refresh errors; UI can keep showing cached data
                }
            },
        );
    }

    const mergeLocalWishlistOnLogin = async () => {
        try {
            // Get current local wishlist items
            localWishlist.getWishlistProducts();
            const localItems = localWishlist.items.value;

            if (localItems.length > 0) {
                // Get current API wishlist to avoid duplicates
                try {
                    await syncWishlist.getWishlistProducts();
                } catch (err: any) {
                    if (isUnauthorizedError(err)) {
                        console.warn('[Wishlist] Unauthorized during merge; skipping API merge and keeping local.');
                        wishlistSidebarStore.triggerWishlistRefresh();
                        return;
                    }
                    throw err;
                }
                const currentApiItems = syncWishlist.items.value;

                // Only try to add items that are not already in API wishlist
                const itemsToAdd = localItems.filter(item => !currentApiItems.includes(item));

                if (itemsToAdd.length > 0) {
                    for (const productId of itemsToAdd) {
                        try {
                            await syncWishlist.addToWishlistSync(productId);
                        } catch (addError: any) {
                            // Silently handle errors for products that may already exist or unauthorized mid-merge
                            if (!isUnauthorizedError(addError)) {
                                // ignore duplicates and minor issues
                            }
                        }
                    }
                }

                // Refresh API wishlist data to get updated state
                try {
                    await syncWishlist.getWishlistProducts();
                    hasLoadedApiWishlist.value = true;
                } catch (err: any) {
                    if (isUnauthorizedError(err)) {
                        // Fall back silently; local already has the items
                    } else {
                        throw err;
                    }
                }

                // Force update of computed properties by triggering reactivity
                await getWishlistProducts();

                // Update local storage with merged data
                const mergedItems = syncWishlist.items.value;
                updateCacheFromItems(mergedItems);

                // Multiple refresh triggers to ensure all components update
                wishlistSidebarStore.triggerWishlistRefresh();

                // Additional refresh after a short delay
                setTimeout(() => {
                    wishlistSidebarStore.triggerWishlistRefresh();
                }, 500);
            } else {
                // Still refresh API data to ensure we have the latest
                try {
                    await syncWishlist.getWishlistProducts();
                    hasLoadedApiWishlist.value = true;
                } catch (err: any) {
                    if (isUnauthorizedError(err)) {
                        // If unauthorized, rely on local
                    } else {
                        throw err;
                    }
                }
                await getWishlistProducts();

                // Trigger refresh even if no local items
                wishlistSidebarStore.triggerWishlistRefresh();
            }
        } catch (error) {
            // Silently handle any errors to prevent blocking navigation
        }
    };

    const addToWishlist = async (id: string) => {
        // Optimistic cache update so badge updates immediately
        const previousCached = [...cachedItems.value];
        if (!cachedItems.value.includes(id)) {
            const next = [...cachedItems.value, id];
            updateCacheFromItems(next);
            if (isLoggedIn.value) prefetchedWishlist.value = [...next];
        }

        try {
            if (isLoggedIn.value) {
                try {
                    // Add to API first
                    await syncWishlist.addToWishlistSync(id);
                    // Add to local storage to keep in sync
                    await localWishlist.addToWishlist(id);
                    // Ensure cache reflects latest known items
                    updateCacheFromItems(Array.from(new Set([...cachedItems.value, id])));
                } catch (err: any) {
                    if (isUnauthorizedError(err)) {
                        await localWishlist.addToWishlist(id);
                        updateCacheFromItems(Array.from(new Set([...cachedItems.value, id])));
                    } else {
                        throw err;
                    }
                }
            } else {
                // Add to local storage only
                await localWishlist.addToWishlist(id);
                updateCacheFromItems(Array.from(new Set([...cachedItems.value, id])));
            }

            // Trigger refresh for all components
            skipNextRefreshFetch.value = true;
            wishlistSidebarStore.triggerWishlistRefresh();
        } catch (error) {
            // Revert optimistic cache update on error
            updateCacheFromItems(previousCached);
            console.error('❌ UnifiedWishlist: Error adding to wishlist:', error);
            throw error;
        }
    };

    const removeFromWishlist = async (id: string) => {
        // Optimistic cache update so badge updates immediately
        const previousCached = [...cachedItems.value];
        if (cachedItems.value.includes(id)) {
            const next = cachedItems.value.filter(x => x !== id);
            updateCacheFromItems(next);
            if (isLoggedIn.value) prefetchedWishlist.value = [...next];
        }

        if (isLoggedIn.value) {
            try {
                // Remove from API
                await syncWishlist.removeFromWishlistSync(id);
                // Remove from local storage to keep in sync
                await localWishlist.removeFromWishlist(id);
                // Refresh API data to ensure consistency
                await syncWishlist.getWishlistProducts();
                hasLoadedApiWishlist.value = true;
                updateCacheFromItems(syncWishlist.items.value);
            } catch (err: any) {
                if (isUnauthorizedError(err)) {
                    await localWishlist.removeFromWishlist(id);
                    localWishlist.getWishlistProducts();
                    updateCacheFromItems(localWishlist.items.value);
                } else {
                    updateCacheFromItems(previousCached);
                    throw err;
                }
            }
        } else {
            // Remove from local storage only
            await localWishlist.removeFromWishlist(id);
            localWishlist.getWishlistProducts();
            updateCacheFromItems(localWishlist.items.value);
        }

        // Trigger refresh for all components
        skipNextRefreshFetch.value = true;
        wishlistSidebarStore.triggerWishlistRefresh();
    };

    const clearWishlist = async () => {
        const previousCached = [...cachedItems.value];
        // Optimistic cache update
        updateCacheFromItems([]);
        prefetchedWishlist.value = [];

        if (isLoggedIn.value) {
            try {
                // Clear both API and local
                const currentItems = [...syncWishlist.items.value];
                for (const id of currentItems) {
                    await syncWishlist.removeFromWishlistSync(id);
                }
                await localWishlist.clearWishlist();
                hasLoadedApiWishlist.value = true;
            } catch (err: any) {
                if (isUnauthorizedError(err)) {
                    await localWishlist.clearWishlist();
                } else {
                    updateCacheFromItems(previousCached);
                    throw err;
                }
            }
        } else {
            // Clear local storage only
            await localWishlist.clearWishlist();
        }

        // Trigger refresh for all components
        skipNextRefreshFetch.value = true;
        wishlistSidebarStore.triggerWishlistRefresh();
    };

    const isInWishlist = (productId: string) => {
        return computed(() => {
            return items.value.includes(productId);
        });
    };

    return {
        items,
        count,
        getWishlistProducts,
        addToWishlist,
        removeFromWishlist,
        clearWishlist,
        isInWishlist, // New method to check if item is in wishlist
        mergeLocalWishlistOnLogin, // New method for merging on login
    };
}
